package com.arena_web.ui.components.chat

import androidx.compose.runtime.*
import com.arena_web.data.dto.ChatListItemDto
import com.arena_web.ui.components.widgets.Divider
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.components.widgets.SkeletonBox
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMLight
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.h2Medium
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextInput

enum class ChatFilter(val label: String) {
    ALL("All"),
    DIRECT("Direct"),
    COMMUNITY("Community")
}

@Composable
fun ChatListPanel(
    chats: List<ChatListItemDto>,
    selectedChatId: String?,
    onlineUserIds: Set<String>,
    isLoading: Boolean,
    searchQuery: String,
    onSearchQueryChanged: (String) -> Unit,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    onLoadMore: () -> Unit,
    onChatSelected: (String) -> Unit,
    onNewChatClick: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    val sportColors = LocalSportColors.current
    var activeFilter by remember { mutableStateOf(ChatFilter.ALL) }
    var newChatHovered by remember { mutableStateOf(false) }
    var searchFocused by remember { mutableStateOf(false) }

    // Search filter is now server-driven (the parent gives us a list that
    // already matches the query); only the tab pill filter stays local since
    // it's a UI toggle, not a fetch parameter.
    val filteredChats = chats.filter { chat ->
        when (activeFilter) {
            ChatFilter.ALL -> true
            ChatFilter.DIRECT -> chat.type.uppercase() == "DIRECT"
            ChatFilter.COMMUNITY -> chat.type.uppercase() == "GROUP"
        }
    }.sortedByDescending { it.lastMessage?.createdAt ?: it.createdAt }

    // Unread counts per filter — computed over the currently-loaded chats.
    // With pagination this is "unread visible in the loaded window" rather
    // than a global count; close enough for the pill badges and avoids an
    // extra summary endpoint.
    val directUnread = chats.filter { it.type.uppercase() == "DIRECT" && it.unreadCount > 0 }.sumOf { it.unreadCount }
    val communityUnread = chats.filter { it.type.uppercase() == "GROUP" && it.unreadCount > 0 }.sumOf { it.unreadCount }

    Column(
        modifier = modifier
            .fillMaxHeight()
            .styleModifier {
                property("flex-shrink", "0")
                property("background",
                    "linear-gradient(215deg, rgba(60, 121, 241, 0.135) 37.5%, rgba(5, 4, 12, 0.03) 61.8%)"
                )
                property("border", "1px solid rgba(41, 52, 138, 0.2)")
                property("border-radius",
                    if (selectedChatId == null) "${s(28)} ${s(28)} ${s(28)} ${s(28)}"
                    else "${s(28)} 0 ${s(28)} ${s(28)}"
                )
                property("overflow", "hidden")
            }
    ) {
        // Title
        SpanText(
            t.chatPanelTitle,
            modifier = Modifier
                .h2Medium()
                .color(Color.white)
                .sPadding(22, 18)
                .styleModifier {
                    property("flex-shrink", "0")
                }
        )

        // Search + New chat button area
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sPadding(0, 18)
                .sGap(18)
                .styleModifier { property("flex-shrink", "0") }
        ) {
            Row(
                modifier = Modifier.fillMaxWidth().sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Search input
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .sHeight(44)
                        .styleModifier { property("position", "relative") }
                ) {
                    // Gradient border layer
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("border", "0.3px solid transparent")
                                property("border-radius", s(28))
                                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("-webkit-mask-composite", "xor")
                                property("mask-composite", "exclude")
                                property("pointer-events", "none")
                            }
                    )
                    // Content
                    Row(
                        modifier = Modifier.fillMaxSize().sPaddingLeft(12).sPaddingRight(8),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            src = AppIcons.Search,
                            modifier = Modifier
                                .opacity(0.5)
                                .styleModifier {
                                    property("flex-shrink", "0")
                                }
                        )
                        TextInput(
                            value = searchQuery,
                            attrs = Modifier
                                .fillMaxWidth()
                                .bodyLRegular()
                                .color(AppColors.Text.Primary)
                                .sPaddingLeft(6)
                                .styleModifier {
                                    property("background", "transparent")
                                    property("border", "none")
                                    property("outline", "none")
                                    property("font-family", "Urbanist, sans-serif")
                                }
                                .toAttrs {
                                    attr("placeholder", t.chatSearchPlaceholder)
                                    onInput { event ->
                                        onSearchQueryChanged(event.target.asDynamic().value as String)
                                    }
                                    onFocusIn { searchFocused = true }
                                    onFocusOut { searchFocused = false }
                                }
                        )
                    }
                }

                // New chat button
                Box(
                    modifier = Modifier
                        .sSize(44)
                        .cursor(Cursor.Pointer)
                        .onClick { onNewChatClick() }
                        .attrsModifier {
                            onMouseEnter { newChatHovered = true }
                            onMouseLeave { newChatHovered = false }
                        }
                        .styleModifier {
                            property("position", "relative")
                            property("flex-shrink", "0")
                        },
                    contentAlignment = Alignment.Center
                ) {
                    // Gradient border layer
                    Box(
                        modifier = Modifier
                            .fillMaxSize()
                            .styleModifier {
                                property("position", "absolute")
                                property("inset", "0")
                                property("border", "0.3px solid transparent")
                                property("border-radius", s(22))
                                property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                                property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                                property("-webkit-mask-composite", "xor")
                                property("mask-composite", "exclude")
                                property("pointer-events", "none")
                            }
                    )
                    // Icon
                    Icon(
                        src = AppIcons.Add,
                        modifier = Modifier
                            .opacity(if (newChatHovered) 1 else 0.5)
                            .styleModifier {
                                property("transition", "all 0.2s ease")
                            }
                    )
                }
            }

            // Segmented control tabs (same style as TimeSwitcher)
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("position", "relative")
                        property("flex-shrink", "0")
                    }
            ) {
                // Gradient border layer
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("position", "absolute")
                            property("inset", "0")
                            property("border", "0.3px solid transparent")
                            property("border-radius", s(34))
                            property("background", "${AppGradients.ButtonBorderGradientPrimary} border-box")
                            property("-webkit-mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("mask", "linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0)")
                            property("-webkit-mask-composite", "xor")
                            property("mask-composite", "exclude")
                            property("pointer-events", "none")
                        }
                )
                // Content
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .sBorderRadius(34)
                        .sPadding(4)
                        .styleModifier {
                            property("background", AppGradients.InteractiveSecondary)
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    ChatFilter.entries.forEach { filter ->
                        val isActive = activeFilter == filter
                        val filterLabel = when (filter) {
                            ChatFilter.ALL -> t.chatFilterAll
                            ChatFilter.DIRECT -> t.chatFilterDirect
                            ChatFilter.COMMUNITY -> t.chatFilterCommunity
                        }

                        val unreadCount = when (filter) {
                            ChatFilter.ALL -> 0L
                            ChatFilter.DIRECT -> directUnread
                            ChatFilter.COMMUNITY -> communityUnread
                        }

                        if (isActive) {
                            Box(
                                Modifier
                                    .weight(1f)
                                    .sHeight(36)
                                    .sBorderRadius(22)
                                    .backgroundColor(sportColors.primary)
                                    .cursor(Cursor.Pointer)
                                    .onClick { activeFilter = filter },
                                contentAlignment = Alignment.Center
                            ) {
                                SpanText(
                                    filterLabel,
                                    modifier = Modifier
                                        .bodyLMedium()
                                        .color(sportColors.onPrimary)
                                )
                            }
                        } else {
                            Row(
                                modifier = Modifier
                                    .weight(1f)
                                    .sHeight(36)
                                    .cursor(Cursor.Pointer)
                                    .onClick { activeFilter = filter }
                                    .sGap(8)
                                    .styleModifier {
                                        property("justify-content", "center")
                                    },
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                SpanText(
                                    filterLabel,
                                    modifier = Modifier
                                        .bodyLMedium()
                                        .color(AppColors.Text.Secondary)
                                )
                                if (unreadCount > 0) {
                                    Box(
                                        modifier = Modifier
                                            .sSize(20)
                                            .borderRadius(50.percent)
                                            .backgroundColor(sportColors.primary),
                                        contentAlignment = Alignment.Center
                                    ) {
                                        SpanText(
                                            if (unreadCount > 99) "99+" else unreadCount.toString(),
                                            modifier = Modifier
                                                .bodyMRegular()
                                                .color(Color.white)
                                                .styleModifier { property("transform", "translateY(1px)") }
                                        )
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }

        // Chat list (scrollable). Scroll listener fires `onLoadMore` once
        // the viewer comes within ~150px of the bottom — same idiom as the
        // followers list / DM picker / FeedFollowSidebar so paging feels
        // consistent across the app.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .sMarginTop(18)
                .sGap(0)
                .styleModifier {
                    property("flex", "1")
                    property("min-height", "0")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                    property("padding", "${s(0)} ${s(18)}")
                }
                .attrsModifier {
                    onScroll { evt ->
                        if (hasMore && !isLoading && !isLoadingMore) {
                            val el = evt.target.unsafeCast<org.w3c.dom.HTMLElement>()
                            val nearBottom =
                                el.scrollHeight - el.scrollTop - el.clientHeight < 150.0
                            if (nearBottom) onLoadMore()
                        }
                    }
                }
        ) {
            if (isLoading && chats.isEmpty()) {
                // Six rows is enough to fill the visible column at the
                // default 360px width. Each row varies its bar widths so the
                // skeleton reads as a list of distinct conversations rather
                // than six photocopies of the same shape.
                val widths = listOf(
                    SkeletonRowWidths(name = 132, preview = 96),
                    SkeletonRowWidths(name = 108, preview = 140),
                    SkeletonRowWidths(name = 148, preview = 88),
                    SkeletonRowWidths(name = 96, preview = 124),
                    SkeletonRowWidths(name = 124, preview = 108),
                    SkeletonRowWidths(name = 140, preview = 80),
                )
                widths.forEach { ChatListItemSkeleton(it) }
            } else if (filteredChats.isEmpty()) {
                Box(
                    modifier = Modifier.fillMaxWidth().sPadding(24, 16),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        if (searchQuery.isNotBlank()) t.chatNoChatsFound
                        else t.chatNoConversationsYet,
                        modifier = Modifier.bodyMRegular().color(AppColors.Text.Muted)
                    )
                }
            } else {
                filteredChats.forEachIndexed { index, chat ->
                    ChatListItem(
                        chat = chat,
                        isSelected = chat.id == selectedChatId,
                        isOnline = isOnline(chat, onlineUserIds),
                        onClick = { onChatSelected(chat.id) }
                    )
                    val nextChat = filteredChats.getOrNull(index + 1)
                    val isNextSelected = nextChat?.id == selectedChatId
                    if (index < filteredChats.lastIndex && chat.id != selectedChatId && !isNextSelected) {
                        Divider()
                    }
                }
                if (isLoadingMore) {
                    Box(
                        modifier = Modifier.fillMaxWidth().sPadding(12, 0),
                        contentAlignment = Alignment.Center
                    ) {
                        InlineSpinner(size = 20.px)
                    }
                }
            }
        }
    }
}

private fun isOnline(chat: ChatListItemDto, onlineUserIds: Set<String>): Boolean {
    if (chat.type.uppercase() != "DIRECT") return false
    val otherId = chat.otherUserId ?: return false
    return onlineUserIds.contains(otherId)
}

private data class SkeletonRowWidths(val name: Int, val preview: Int)

@Composable
private fun ChatListItemSkeleton(widths: SkeletonRowWidths) {
    // Same frame as a real ChatListItem row — 3px transparent border, the
    // same paddings + 8px gap, a 36×36 round avatar (matching the fallback
    // round Avatar used when the chat has no generated player card — that's
    // the common case while skeletons are visible since the player-card
    // hydrate races with the list fetch), and two short text bars.
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("border", "3px solid transparent")
                property("border-radius", s(14))
                property("padding", "${s(12)} ${s(16)} ${s(12)} ${s(12)}")
                property("gap", s(8))
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Wrap the avatar in a `flex-shrink: 0` Box so the surrounding flex
        // row can't squash it into an ellipse when the text column wants
        // more width than is available.
        Box(
            modifier = Modifier.styleModifier { property("flex-shrink", "0") }
        ) {
            SkeletonBox(width = 36.px, height = 36.px, borderRadius = 50.percent)
        }
        Column(
            modifier = Modifier
                .weight(1f)
                .styleModifier {
                    property("min-width", "0")
                    property("gap", s(4))
                }
        ) {
            SkeletonBox(width = widths.name.px, height = 14.px, borderRadius = 7.px)
            SkeletonBox(width = widths.preview.px, height = 10.px, borderRadius = 5.px)
        }
    }
}





